import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import useAuthentication from "../../Infrastructure/States/onAuthStateChange";
import {
  chooseModalEmailVerify,
  chooseModalLogin,
} from "../../Infrastructure/States/authModalsSlice";
import { useDispatch, useSelector } from "react-redux";
import { ShowErrorToast } from "../Common/Toast/toast";
import { Loader } from "../Common/Loader/loader";
import "@whereby.com/browser-sdk/embed";
import paths from "../../Infrastructure/Router/paths";
import { USER_LOGS_CLIENT } from "../../Infrastructure/Constants/logsConstant";
import { safeClientLogEvent } from "../../Infrastructure/Utilities/safeClientLogEvent";
import conferenceRoomSx from "./styles";

export const ConferenceRoom = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { classUrl } = useParams();
  const [classData, setClassData] = useState(null);
  const { user, userId, loading } = useAuthentication();
  const [roomLoading, setRoomLoading] = useState(true);
  const userData = useSelector((state) => state.userData.data);
  const email_verified = userData?.email_verified
  const userDataLoading = useSelector((state) => state.userData.loading);
  const coursesData = useSelector((state) => state.userData.coursesData);
  const learnerRoomUrl = coursesData && coursesData?.[classData?.course_id]?.courseDetails?.class_schedule[classData?.class_id]?.learner_room_url

  const participantName = `${userData?.account?.first_name}${" "}${userData?.account?.last_name}`

  useEffect(() => {
    if (!loading && !userDataLoading && user) {
      if (typeof email_verified === "boolean") {
        if (!email_verified) {
          navigate('/')
          dispatch(chooseModalEmailVerify());
        }
      }
    } else if (!loading && !userDataLoading && !user) {
      navigate('/')
      dispatch(chooseModalLogin());
    }
  }, [loading, user, dispatch, navigate, email_verified, userDataLoading])

  useEffect(() => {
    if (userData) {
      const fetchStudentData = async () => {
        try {
          if (classUrl && userData?.is_educator) {
            // Safely handle educator's courses
            const courses = userData?.educator?.courses || {};
            const enrolledClass = Object.values(courses)
              .filter((course) => course?.status === "active")
              .map((course) => Object.values(course?.courseDetails?.class_schedule || {}))
              .flat()
              .find((cls) => cls?.status === "active" && cls?.class_url === classUrl);
            if (!enrolledClass) {
              ShowErrorToast("Class not found or does not belong to you.");
              navigate(paths.dashboardPage.path + "#classes");
              return;
            }
            setClassData(enrolledClass);

          } else {
            // Safely handle student's classes
            const classes = userData?.student?.classes || {};
            const enrolledClass = Object.values(classes).find(
              (cls) => cls?.class_url === classUrl
            );
            if (!enrolledClass) {
              ShowErrorToast("You are not enrolled in this class.");
              navigate(paths.dashboardPage.path + "#classes");
              return;
            }
            const currentTimestamp = Math.floor(Date.now());
            const firstClass = enrolledClass.first_class;
            const lastClass = enrolledClass.last_class;

            if (currentTimestamp < firstClass || currentTimestamp > lastClass) {
              ShowErrorToast("Class is not in session.");
              navigate(paths.dashboardPage.path + "#classes");
              return;
            }
            setClassData(enrolledClass);
          }
        } catch (error) {
          ShowErrorToast("An error occurred. Please try again later.");
          await safeClientLogEvent({
            ...USER_LOGS_CLIENT.CONFERENCE_ROOM_FETCH_FAILED,
            uid: userId || null,
            source: "conferenceRoom.jsx",
            emailInfo: null,
            metadata: null,
            error: error?.message || null
          })
          navigate(paths.dashboardPage.path + "#classes");
        }
      };
      fetchStudentData();
    }
  }, [userId, classUrl, userData, navigate]);

  const roomUrl = userData?.is_educator
    ? classData?.educator_room_url
    : learnerRoomUrl

  useEffect(() => {
    if (roomUrl) {
      const timeout = setTimeout(() => {
        setRoomLoading(false);
      }, 1000)

      return () => clearTimeout(timeout);
    }
  }, [roomUrl]);
  if (loading || userDataLoading || !classData || !roomUrl) {
    return (
        <Loader height={"100vh"} />
    );
  }

  return (
    <Box sx={conferenceRoomSx.root}>
      <Box sx={conferenceRoomSx.backArrowBox}>
        <FaArrowLeft onClick={() => navigate(-1)} color="white" />
      </Box>
      {roomLoading && (
        <Box sx={conferenceRoomSx.loadingOverlay}>
         <Box> 
          <Loader/>
          <Typography fontWeight={500} color="#333">
            Connecting to your classroom...
          </Typography>
          <Typography color="text.secondary" mt={1}>
            Please wait a moment while we prepare your session.
          </Typography>
          </Box>
        </Box>
      )}
      {participantName && roomUrl && <whereby-embed room={roomUrl} displayName={participantName} style={{
        height: "100%",
        width: "100%",
        position: "relative",
        zIndex: 1,
      }} />}
    </Box>
  );
}